import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Coins,
  Gift,
  Users,
  Trophy,
  CheckCircle,
  ChevronRight,
  Loader2,
  X,
  Share2,
  UserPlus,
  Target,
  Star,
  Sparkles,
  Package,
  MapPin,
  ArrowLeft,
  Copy,
  MessageCircle,
  Mail,
  ShoppingCart,
  Zap,
  CreditCard,
  Award
} from 'lucide-react';
import { Button } from './ui/button';
import InviteShareCard from './InviteShareCard';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const GolfCoinsPage = ({ user }) => {
  const navigate = useNavigate();
  const { searchParams } = new URL(window.location.href);
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(0);
  const [tasks, setTasks] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [packages, setPackages] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showRedeemModal, setShowRedeemModal] = useState(false);
  const [selectedReward, setSelectedReward] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [activeTab, setActiveTab] = useState('earn'); // earn, buy, redeem, history
  const [purchasing, setPurchasing] = useState(false);
  
  // Shipping form state
  const [shippingForm, setShippingForm] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    phone: ''
  });
  const [redeeming, setRedeeming] = useState(false);

  useEffect(() => {
    if (user) {
      loadGolfCoinsData();
    }
  }, [user]);

  // Handle tab from URL param
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && ['earn', 'buy', 'use', 'history'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, []);

  // Handle purchase return
  useEffect(() => {
    const purchaseResult = searchParams.get('purchase');
    const purchaseId = searchParams.get('purchase_id');
    if (purchaseResult === 'success' && purchaseId) {
      verifyPurchase(purchaseId);
      window.history.replaceState({}, '', '/golf-coins');
    } else if (purchaseResult === 'cancelled') {
      toast.info('Purchase cancelled');
      window.history.replaceState({}, '', '/golf-coins');
    }
  }, []);

  const loadGolfCoinsData = async () => {
    try {
      setLoading(true);
      const [balanceRes, tasksRes, rewardsRes, packagesRes] = await Promise.all([
        axios.get(`${API}/golf-coins/balance`),
        axios.get(`${API}/golf-coins/tasks`),
        axios.get(`${API}/golf-coins/rewards`),
        axios.get(`${API}/golf-coins/packages`)
      ]);
      
      setBalance(balanceRes.data.balance || 0);
      setTransactions(balanceRes.data.transactions || []);
      setTasks(tasksRes.data.tasks || []);
      setRewards(rewardsRes.data.rewards || []);
      setPackages(packagesRes.data.packages || []);
      
      // Show onboarding if first time
      if (balanceRes.data.show_onboarding) {
        setShowOnboarding(true);
      }
      
      // Generate invite link
      if (balanceRes.data.invite_code) {
        const baseUrl = window.location.origin;
        setInviteCode(balanceRes.data.invite_code);
        setInviteLink(`${baseUrl}/invite/${balanceRes.data.invite_code}`);
      }
    } catch (error) {
      console.error('Error loading golf coins:', error);
      if (error.response?.status === 401) {
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const verifyPurchase = async (purchaseId) => {
    try {
      const res = await axios.get(`${API}/golf-coins/verify-purchase/${purchaseId}`);
      if (res.data.success) {
        if (res.data.already_processed) {
          toast.info('Purchase already processed');
        } else {
          toast.success(`+${res.data.coins} Golf Coins added!`);
        }
        loadGolfCoinsData();
      }
    } catch (error) {
      console.error('Error verifying purchase:', error);
      toast.error('Failed to verify purchase');
    }
  };

  const handlePurchase = async (packageId) => {
    try {
      setPurchasing(true);
      const res = await axios.post(`${API}/golf-coins/purchase`, { package_id: packageId });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start purchase');
    } finally {
      setPurchasing(false);
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      const res = await axios.post(`${API}/golf-coins/complete-task`, { task_id: taskId });
      if (res.data.success) {
        toast.success(`+${res.data.coins_earned} Golf Coins!`);
        setBalance(res.data.new_balance);
        // Refresh tasks
        const tasksRes = await axios.get(`${API}/golf-coins/tasks`);
        setTasks(tasksRes.data.tasks || []);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete task');
    }
  };

  const handleRedeem = async () => {
    if (!selectedReward) return;
    
    // Validate shipping form
    if (!shippingForm.name || !shippingForm.address || !shippingForm.city || 
        !shippingForm.state || !shippingForm.zip) {
      toast.error('Please fill in all shipping details');
      return;
    }
    
    try {
      setRedeeming(true);
      const res = await axios.post(`${API}/golf-coins/redeem`, {
        reward_id: selectedReward.reward_id,
        shipping: shippingForm
      });
      
      if (res.data.success) {
        toast.success('Redemption successful! Your reward is on its way!');
        setBalance(res.data.new_balance);
        setShowRedeemModal(false);
        setSelectedReward(null);
        setShippingForm({ name: '', address: '', city: '', state: '', zip: '', phone: '' });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to redeem');
    } finally {
      setRedeeming(false);
    }
  };

  const copyInviteLink = () => {
    navigator.clipboard.writeText(inviteLink);
    toast.success('Invite link copied!');
  };

  const shareInvite = (method) => {
    const message = `Join me on Par Seeker - the ultimate golf app! Track scores, play games with friends, and earn rewards. Use my link: ${inviteLink}`;
    
    if (method === 'sms') {
      window.open(`sms:?body=${encodeURIComponent(message)}`);
    } else if (method === 'email') {
      window.open(`mailto:?subject=Join me on Par Seeker!&body=${encodeURIComponent(message)}`);
    } else if (method === 'share') {
      if (navigator.share) {
        navigator.share({ title: 'Join Par Seeker', text: message, url: inviteLink });
      } else {
        copyInviteLink();
      }
    }
  };

  const completeOnboarding = async () => {
    try {
      await axios.post(`${API}/golf-coins/complete-onboarding`);
      setShowOnboarding(false);
      loadGolfCoinsData();
    } catch (error) {
      setShowOnboarding(false);
    }
  };

  if (loading) {
    return (
      <div className="ps-theme-page min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-amber-500 mx-auto mb-3" />
          <p className="text-gray-400">Loading Golf Coins...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ps-theme-page min-h-screen pb-24" data-testid="golf-coins-page">
      {/* Header */}
      <div className="ps-page-header px-4 pt-4 pb-6" data-testid="golf-coins-header">
        <div className="flex items-center mb-6">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 hover:bg-white/10 rounded-full">
            <ArrowLeft className="w-5 h-5 text-gray-400" />
          </button>
          <h1 className="text-xl font-bold text-white ml-2">Golf Coins</h1>
        </div>
        
        {/* Balance Card */}
        <div className="ps-gold-balance bg-gradient-to-br from-amber-500 to-yellow-600 rounded-3xl p-6 text-center shadow-lg shadow-amber-500/20" data-testid="golf-coins-balance-panel">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Coins className="w-8 h-8 text-amber-900" />
            <span className="text-amber-900/70 font-medium">Your Balance</span>
          </div>
          <p className="text-5xl font-bold text-white mb-2">{balance.toLocaleString()}</p>
          <p className="text-amber-100 text-sm">Golf Coins</p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b border-gray-800 px-2 overflow-x-auto">
        {[
          { id: 'earn', label: 'Earn', icon: Target },
          { id: 'buy', label: 'Buy', icon: ShoppingCart },
          { id: 'use', label: 'Use Coins', icon: Zap },
          { id: 'history', label: 'History', icon: Trophy }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 py-3 flex items-center justify-center gap-1.5 border-b-2 transition whitespace-nowrap px-2 ${
              activeTab === tab.id 
                ? 'border-amber-500 text-amber-500' 
                : 'border-transparent text-gray-500 hover:text-gray-300'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            <span className="font-medium text-sm">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="px-4 py-4">
        {activeTab === 'earn' && (
          <div className="space-y-4">
            {/* Leaderboard Link */}
            <button
              data-testid="golf-coins-leaderboard-link"
              onClick={() => navigate('/leaderboard')}
              className="w-full bg-gradient-to-r from-amber-900/30 to-orange-900/30 border border-amber-500/30 rounded-2xl p-4 flex items-center gap-4 hover:border-amber-500/50 transition"
            >
              <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center text-2xl">
                🏆
              </div>
              <div className="flex-1 text-left">
                <p className="text-white font-semibold">Weekly Leaderboard</p>
                <p className="text-gray-400 text-sm">See top coin earners this week</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-500" />
            </button>

            {/* Achievements Badge Link */}
            <button
              data-testid="golf-coins-achievements-link"
              onClick={() => navigate('/achievements')}
              className="w-full bg-gradient-to-r from-purple-900/30 to-indigo-900/30 border border-purple-500/30 rounded-2xl p-4 flex items-center gap-4 hover:border-purple-500/50 transition"
            >
              <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                <Award className="w-6 h-6 text-purple-400" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-white font-semibold">Achievements & Badges</p>
                <p className="text-gray-400 text-sm">Unlock badges and earn bonus coins</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-500" />
            </button>

            {/* Invite Friends Card */}
            <InviteShareCard inviteCode={inviteCode || 'LOADING'} userName={user?.name} />

            {/* Other Tasks */}
            <h3 className="text-gray-400 font-medium text-sm uppercase tracking-wide mt-6 mb-3">Complete Tasks</h3>
            <div className="space-y-3">
              {tasks.map((task) => (
                <div 
                  key={task.task_id}
                  className={`bg-gray-900/50 border rounded-xl p-4 ${
                    task.completed 
                      ? 'border-emerald-500/30 opacity-60' 
                      : 'border-gray-800 hover:border-gray-700'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      task.completed ? 'bg-emerald-500/20' : 'bg-gray-800'
                    }`}>
                      {task.completed ? (
                        <CheckCircle className="w-6 h-6 text-emerald-500" />
                      ) : (
                        <TaskIcon type={task.type} />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className={`font-medium ${task.completed ? 'text-gray-400 line-through' : 'text-white'}`}>
                        {task.title}
                      </p>
                      <p className="text-gray-500 text-sm">{task.description}</p>
                    </div>
                    <div className="text-right">
                      {task.completed ? (
                        <span className="text-emerald-500 text-sm font-medium">Completed</span>
                      ) : (
                        <>
                          <p className="text-amber-400 font-bold">+{task.coins}</p>
                          {task.action_url && (
                            <button 
                              onClick={() => navigate(task.action_url)}
                              className="text-emerald-400 text-xs hover:underline"
                            >
                              Go →
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'buy' && (
          <div className="space-y-4">
            <div className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 border border-purple-500/30 rounded-2xl p-4 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-500/20 rounded-full flex items-center justify-center">
                  <Zap className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-white font-semibold">Instant Coins</p>
                  <p className="text-gray-400 text-sm">Buy coins to unlock rewards faster</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-3">
              {packages.map((pkg) => (
                <button
                  key={pkg.package_id}
                  onClick={() => handlePurchase(pkg.package_id)}
                  disabled={purchasing}
                  className={`w-full bg-gray-900/50 border rounded-xl p-4 flex items-center gap-4 transition ${
                    pkg.popular 
                      ? 'border-amber-500 hover:border-amber-400' 
                      : 'border-gray-800 hover:border-gray-700'
                  }`}
                >
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                    pkg.popular ? 'bg-amber-500/20' : 'bg-gray-800'
                  }`}>
                    <Coins className={`w-7 h-7 ${pkg.popular ? 'text-amber-400' : 'text-gray-400'}`} />
                  </div>
                  <div className="flex-1 text-left">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-semibold">{pkg.label}</span>
                      {pkg.popular && (
                        <span className="bg-amber-500 text-amber-900 text-xs font-bold px-2 py-0.5 rounded-full">POPULAR</span>
                      )}
                      {pkg.bonus && !pkg.popular && (
                        <span className="bg-emerald-500/20 text-emerald-400 text-xs font-medium px-2 py-0.5 rounded-full">{pkg.bonus}</span>
                      )}
                    </div>
                    <p className="text-gray-500 text-sm">{pkg.coins.toLocaleString()} Golf Coins</p>
                  </div>
                  <div className="text-right">
                    <p className="text-white font-bold text-lg">${(pkg.price / 100).toFixed(2)}</p>
                    <p className="text-gray-500 text-xs">${(pkg.price / pkg.coins * 100).toFixed(1)}¢/coin</p>
                  </div>
                </button>
              ))}
            </div>
            
            <div className="mt-6 p-4 bg-gray-900/30 rounded-xl">
              <div className="flex items-start gap-3">
                <CreditCard className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-gray-300 text-sm font-medium">Secure Payment</p>
                  <p className="text-gray-500 text-xs">Powered by Stripe. Your payment info is never stored.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'use' && (
          <div className="space-y-4">
            <p className="text-gray-400 text-sm mb-4">
              Use your Golf Coins for tournament entry fees and in-app upgrades!
            </p>
            
            {/* Tournament Entry */}
            <div className="bg-gradient-to-br from-green-900/50 to-emerald-900/30 border border-green-700/50 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 bg-green-600/30 rounded-full flex items-center justify-center">
                  <Trophy className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg">Tournament Entry</h3>
                  <p className="text-green-400 text-sm">Play for prizes</p>
                </div>
              </div>
              <p className="text-gray-300 text-sm mb-4">
                Enter Skins, Match Play, and Stroke Play tournaments using Golf Coins instead of real money. Win coins from other players!
              </p>
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="bg-green-900/50 text-green-300 px-3 py-1 rounded-full text-xs">Skins Games</span>
                <span className="bg-green-900/50 text-green-300 px-3 py-1 rounded-full text-xs">Match Play</span>
                <span className="bg-green-900/50 text-green-300 px-3 py-1 rounded-full text-xs">Stroke Play</span>
              </div>
              <button
                onClick={() => window.location.href = '/games'}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 rounded-xl transition"
              >
                Browse Games
              </button>
            </div>

            {/* Coming Soon - In-App Upgrades */}
            <div className="bg-gray-900/50 border border-gray-700 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 bg-purple-600/30 rounded-full flex items-center justify-center">
                  <Zap className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg">In-App Upgrades</h3>
                  <p className="text-purple-400 text-sm">Coming Soon</p>
                </div>
              </div>
              <p className="text-gray-400 text-sm mb-4">
                Unlock premium features with your Golf Coins:
              </p>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-gray-400">
                  <div className="w-1.5 h-1.5 bg-purple-500 rounded-full"></div>
                  <span>Advanced handicap analytics</span>
                </div>
                <div className="flex items-center gap-2 text-gray-400">
                  <div className="w-1.5 h-1.5 bg-purple-500 rounded-full"></div>
                  <span>Custom scorecard themes</span>
                </div>
                <div className="flex items-center gap-2 text-gray-400">
                  <div className="w-1.5 h-1.5 bg-purple-500 rounded-full"></div>
                  <span>Priority matchmaking</span>
                </div>
                <div className="flex items-center gap-2 text-gray-400">
                  <div className="w-1.5 h-1.5 bg-purple-500 rounded-full"></div>
                  <span>Extended score history</span>
                </div>
                <div className="flex items-center gap-2 text-gray-400">
                  <div className="w-1.5 h-1.5 bg-purple-500 rounded-full"></div>
                  <span>Ad-free experience</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <div className="space-y-3">
            {transactions.length === 0 ? (
              <div className="text-center py-12">
                <Trophy className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">No transactions yet</p>
                <p className="text-gray-500 text-sm">Start earning Golf Coins!</p>
              </div>
            ) : (
              transactions.map((tx, idx) => (
                <div key={tx.transaction_id || idx} className="bg-gray-900/50 border border-gray-800 rounded-xl p-4">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      tx.type === 'earn' || tx.type === 'purchase' ? 'bg-emerald-500/20' : 'bg-red-500/20'
                    }`}>
                      {tx.type === 'earn' ? (
                        <Sparkles className="w-5 h-5 text-emerald-500" />
                      ) : tx.type === 'purchase' ? (
                        <ShoppingCart className="w-5 h-5 text-emerald-500" />
                      ) : (
                        <Gift className="w-5 h-5 text-red-400" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-medium">{tx.description}</p>
                      <p className="text-gray-500 text-sm">{formatDate(tx.created_at)}</p>
                    </div>
                    <p className={`font-bold ${tx.type === 'earn' || tx.type === 'purchase' ? 'text-emerald-400' : 'text-red-400'}`}>
                      {tx.type === 'earn' || tx.type === 'purchase' ? '+' : '-'}{tx.amount.toLocaleString()}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Onboarding Modal */}
      {showOnboarding && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-3xl w-full max-w-md overflow-hidden">
            <div className="bg-gradient-to-br from-amber-500 to-yellow-600 p-8 text-center">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Coins className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Welcome to Golf Coins!</h2>
              <p className="text-amber-100">Earn rewards just for using Par Seeker</p>
            </div>
            
            <div className="p-6">
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-emerald-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <UserPlus className="w-4 h-4 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Invite Friends</p>
                    <p className="text-gray-400 text-sm">Earn 500 coins for each friend who joins</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Target className="w-4 h-4 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Complete Tasks</p>
                    <p className="text-gray-400 text-sm">Earn coins for using app features</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-amber-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Gift className="w-4 h-4 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Redeem Rewards</p>
                    <p className="text-gray-400 text-sm">Trade coins for exclusive Par Seeker merch</p>
                  </div>
                </div>
              </div>
              
              <Button 
                onClick={completeOnboarding}
                className="w-full bg-amber-500 hover:bg-amber-600 text-amber-900 font-bold py-4 rounded-xl"
              >
                Start Earning!
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/80 flex items-end sm:items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-t-3xl sm:rounded-3xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Invite Friends</h2>
                <button onClick={() => setShowInviteModal(false)} className="p-2 hover:bg-gray-800 rounded-full">
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
              
              <div className="bg-emerald-900/30 border border-emerald-500/30 rounded-xl p-4 mb-6 text-center">
                <p className="text-emerald-400 font-semibold mb-1">Earn 500 Golf Coins</p>
                <p className="text-gray-400 text-sm">for each friend who signs up!</p>
              </div>
              
              <div className="mb-6">
                <label className="block text-gray-400 text-sm mb-2">Your Invite Link</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={inviteLink}
                    readOnly
                    className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white text-sm"
                  />
                  <button 
                    onClick={copyInviteLink}
                    className="bg-gray-800 border border-gray-700 rounded-lg px-4 hover:bg-gray-700"
                  >
                    <Copy className="w-5 h-5 text-gray-400" />
                  </button>
                </div>
              </div>
              
              <div className="space-y-3">
                <button 
                  onClick={() => shareInvite('sms')}
                  className="w-full flex items-center gap-4 bg-gray-800 hover:bg-gray-700 rounded-xl p-4 transition"
                >
                  <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                    <MessageCircle className="w-5 h-5 text-green-400" />
                  </div>
                  <span className="text-white font-medium">Send via Text Message</span>
                  <ChevronRight className="w-5 h-5 text-gray-500 ml-auto" />
                </button>
                
                <button 
                  onClick={() => shareInvite('email')}
                  className="w-full flex items-center gap-4 bg-gray-800 hover:bg-gray-700 rounded-xl p-4 transition"
                >
                  <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
                    <Mail className="w-5 h-5 text-blue-400" />
                  </div>
                  <span className="text-white font-medium">Send via Email</span>
                  <ChevronRight className="w-5 h-5 text-gray-500 ml-auto" />
                </button>
                
                <button 
                  onClick={() => shareInvite('share')}
                  className="w-full flex items-center gap-4 bg-gray-800 hover:bg-gray-700 rounded-xl p-4 transition"
                >
                  <div className="w-10 h-10 bg-purple-500/20 rounded-full flex items-center justify-center">
                    <Share2 className="w-5 h-5 text-purple-400" />
                  </div>
                  <span className="text-white font-medium">Share Link</span>
                  <ChevronRight className="w-5 h-5 text-gray-500 ml-auto" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper component for task icons
const TaskIcon = ({ type }) => {
  const icons = {
    profile: <Users className="w-6 h-6 text-blue-400" />,
    score: <Target className="w-6 h-6 text-emerald-400" />,
    buddy: <UserPlus className="w-6 h-6 text-purple-400" />,
    game: <Trophy className="w-6 h-6 text-amber-400" />,
    wallet: <Coins className="w-6 h-6 text-green-400" />,
    share: <Share2 className="w-6 h-6 text-pink-400" />,
    default: <Star className="w-6 h-6 text-gray-400" />
  };
  return icons[type] || icons.default;
};

// Date formatter
const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export default GolfCoinsPage;
